import { useState } from 'react'
import { assetUrl } from '../utils/assetUrl'

export interface ConditionPhoto {
  url: string
  takenBy: 'renter' | 'owner' | null
  takenAt: string | null
}

interface Props {
  atPickup: ConditionPhoto[]
  atReturn: ConditionPhoto[]
}

const whoTook = (p: ConditionPhoto) => (p.takenBy === 'renter' ? 'Taken by the renter' : p.takenBy === 'owner' ? 'Taken by the owner' : 'Photo')

const whenTook = (p: ConditionPhoto) =>
  p.takenAt ? new Date(p.takenAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : ''

// The photos taken when the item left the owner and when it came back, so either person can
// point to them if the item's condition is ever in question.
export default function ConditionPhotos({ atPickup, atReturn }: Props) {
  const [open, setOpen] = useState<ConditionPhoto | null>(null)

  if (atPickup.length === 0 && atReturn.length === 0) return null

  const group = (title: string, empty: string, photos: ConditionPhoto[]) => (
    <div>
      <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-2">{title}</h3>
      {photos.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">{empty}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((p) => (
            <button
              key={p.url}
              type="button"
              onClick={() => setOpen(p)}
              aria-label={`View photo. ${whoTook(p)}`}
              className="text-left min-h-[44px] rounded-lg border border-[var(--border)] overflow-hidden bg-white"
            >
              <img src={assetUrl(p.url)} alt={whoTook(p)} className="w-full h-32 object-cover" loading="lazy" />
              <div className="px-2 py-1">
                <div className="text-xs text-[var(--text-primary)]">{whoTook(p)}</div>
                {whenTook(p) && <div className="text-xs text-[var(--text-muted)]">{whenTook(p)}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="bg-white rounded-xl border border-[var(--border)] p-6" data-testid="condition-photos">
      <h2 className="text-lg font-bold text-[var(--text-primary)] mb-1">Condition photos</h2>
      <p className="text-sm text-[var(--text-secondary)] mb-4">
        Photos of the item when it was picked up and when it came back. Both of you can see them.
      </p>
      <div className="space-y-5">
        {group('When it was picked up', 'No photos taken at pickup.', atPickup)}
        {group('When it was returned', 'No return photos yet.', atReturn)}
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Condition photo"
          className="fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center p-4"
          onClick={() => setOpen(null)}
        >
          <img src={assetUrl(open.url)} alt={whoTook(open)} className="max-h-[80vh] max-w-full rounded-lg" />
          <div className="text-white text-sm mt-3">{whoTook(open)}{whenTook(open) ? ` · ${whenTook(open)}` : ''}</div>
          <button type="button" className="btn btn-secondary mt-4 min-h-[44px]" onClick={() => setOpen(null)}>
            Close
          </button>
        </div>
      )}
    </div>
  )
}
